<template>
    <v-container fluid class="pa-0">

        <v-row>
            <!-- HEADER -->
            <v-col cols="10" class="text-left pt-0 mb-5">
                <span class="pb-3 title">Bulk import {{ data.length.toLocaleString() }} panelists</span>
            </v-col>

            <v-col cols="2" class="text-right pt-0">
                <help-icon help="panelist-importer-column-mapping"></help-icon>
            </v-col>

        </v-row>

        <v-row>

            <!-- WARNING FOR MINIMUM VARIABLES -->
            <v-col sm="12" v-if="minVariablesMissing.length > 0">
                <v-alert type="warning" class="body-2" icon="mdi-alert">
                    <span v-if="minVariablesMissing.length > 1">
                        Required data variables {{ minVariablesMissing.join(', ') }} are missing.
                    </span>
                    <span>
                        Required data variable {{ minVariablesMissing.join(', ') }} is missing.
                    </span>
                    All imports require FIRST_NAME, LAST_NAME and EMAIL data variables to be mapped.
                </v-alert>
            </v-col>
        </v-row>

        <v-row class="pt-0">
            <!-- MAPPING ITEMS -->
            <v-col sm="12" v-for="(mapping, index) in mappings" :key="index" class="pt-0">
                <mapping-item
                    v-if="mapping"
                    :mapping-config="mapping"
                    :data-variables="mappableDataVariables"
                    @change="mappingChanged"
                    @config="configChanged"
                ></mapping-item>
            </v-col>
        </v-row>

        <v-row>
            <!-- BUTTONS -->
            <v-col cols="12" class="text-left">

                <v-btn color="primary" :disabled="unconfirmedItems" @click="continueClicked()">Continue
                </v-btn>
                <v-btn @click="$emit('reset')" text>Restart</v-btn>
            </v-col>

            <json v-if="false" :data="mappings"></json>

        </v-row>
    </v-container>
</template>

<script>

import MappingItem from './MappingItem.vue';
import Json from 'vue-json-pretty';
import {mapState, mapMutations, mapGetters, mapActions} from 'vuex';
import {findBestMatch} from 'string-similarity';
import ValidatorFactory from '../ts/ValidatorFactory';
import DetectorFactory from '../ts/DetectorFactory';
import HelpIcon from "../../../components/UI/HelpIcon";

const vuexModule = 'PanelistImporter';
const minMatchScore = 0.5;

export default {

    components: {
        HelpIcon,
        Json,
        MappingItem,
    },

    props: {
        data: {
            type: Array,
            required: true,
        },
        headers: {
            type: Array,
            required: true,
        },
        dataVariables: {
            type: Array,
            required: true
        }
    },

    data() {
        return {
            mappingTable: null
        }
    },

    computed: {

        ...mapState(vuexModule, [
            'mappings',
            'rawMappingTable'
        ]),

        unconfirmedItems() {
            return !!this.mappings.find(mapping => {
                return mapping.column.userActions.confirmed === false && mapping.column.userActions.ignore === false;
            });
        },

        mappableDataVariables() {
            return this.dataVariables.filter(dataVariable => dataVariable.create_writable === true);
        },

        minVariablesMissing() {
            const required = this.dataVariables.filter(variable => ['FIRST_NAME', 'LAST_NAME', 'EMAIL'].includes(variable.name));

            const missing = required.filter(variable => {
                return !this.mappings.find(mapping => mapping.variable.id === variable.id);
            });

            return missing.map(variable => variable.name);
        }
    },

    mounted() {

        // Create VUEX independent copy for performance reasons
        this.mappingTable = JSON.parse(JSON.stringify(this.rawMappingTable));

        // Don't rerun if changing pages
        if (this.mappings.length === 0) {

            this.detectColumnTypes();
            this.autoMapColumns();
        }
    },

    methods: {

        ...mapMutations(vuexModule, [
            'addMappingItem',
            'updateMappingItem'
        ]),

        configChanged(mapping) {
            this.updateMappingItem(mapping);
        },

        continueClicked() {
            this.$emit('complete');
        },

        mappingChanged(mapping) {

            // TODO: Why track auto mapping?
            mapping.column.metadata.autoMapped = false;
            mapping.column.metadata.valueCount = 0;
            mapping.column.metadata.errorCount = 0;
            mapping.column.metadata.nullCount = 0;
            mapping.column.metadata.valuePercent = 0;
            mapping.column.metadata.errorMessages = [];

            if (mapping.variable.id) {

                const dataVariable = this.dataVariables.find((dataVariable => dataVariable.id === mapping.variable.id));
                this.validate(dataVariable, mapping);
            }

            this.updateMappingItem(mapping);
        },

        validate: function (dataVariable, mapping) {

            let validator = ValidatorFactory.getInstance(dataVariable);

            this.data.forEach((lineData, lineNumber) => {

                // Cast to string i.e. number would produce numeric type
                validator.validate('' + lineData[mapping.column.index], lineNumber);
            });

            mapping.column.metadata = Object.assign(mapping.column.metadata, validator.getResults());
        },

        getPossibleVariableTypes(possibleTypes) {
            let types = [];

            possibleTypes.forEach(rawType => {
                let found = this.mappingTable.find(mapping => mapping.raw === rawType);

                if (!found) {
                    throw new Error("Column raw type to data variable mapping table does contain raw type: " + rawType);
                }
                types = [...types, ...found.variableTypes];
            });
            return types;
        },

        getNewMappingItem() {

            const mappingItem = {
                column: {
                    index: null,
                    name: null,
                    optionName: null,     // Null if not checkbox
                    userActions: {
                        ignore: false,
                        skipValueErrors: false,
                        confirmed: false,
                    },
                    metadata: {
                        autoMapped: false,
                        valueCount: 0,
                        errorCount: 0,
                        nullCount: 0,
                        valuePercent: 0,
                        errorMessages: [],
                        bestType: null,
                        possibleTypes: [],
                    }
                },
                variable: {
                    id: null,
                    name: null,
                    type: null,
                    possibleTypes: [],
                    optionId: null,     // Null if not checkbox
                    optionName: null    // Null if not checkbox
                }
            };

            return Object.assign({}, mappingItem);
        },

        detectColumnTypes() {

            let maxLinesToScan = 20;

            if (this.data.length < 20) {
                maxLinesToScan = this.data.length;
            }

            // Test each column
            this.headers.forEach((name, index) => {

                // Try all detector unless a good one is found
                let detectors = DetectorFactory.getAllInstances();

                let detectorCounts = [];

                for (let i = 0; i < detectors.length; i++) {

                    let detector = detectors[i];

                    detector.check(index, this.data, maxLinesToScan);
                    detectorCounts.push(detector.getResults());

                    // No more testing needed if all lines match
                    // Order of detectors is important
                    if (detector.matches === maxLinesToScan) break;
                }

                // Get 50% or higher matches only
                detectorCounts = detectorCounts.filter(detector => {
                    return detector.matches / maxLinesToScan > 0.5;
                });

                // Order by the highest match
                detectorCounts.sort((a, b) => {
                    return b.matches - a.matches;
                });

                let mapping = this.getNewMappingItem();

                mapping.column.index = index;

                mapping.column.metadata.bestType = detectorCounts[0].class;
                mapping.column.metadata.possibleTypes = detectorCounts.map(detector => detector.class);

                const parts = name.split(":");

                // If checkbox column found
                if (parts.length === 2) {
                    mapping.column.name = parts[0].trim().toUpperCase();
                    mapping.column.optionName = parts[1].trim();
                } else {
                    mapping.column.name = name.trim().toUpperCase();
                }

                this.addMappingItem(mapping);
            });
        },

        autoMapColumns() {
            this.mappings.forEach(mapping => {
                this.findBestMatchingVariable(mapping, true);
            });
        },

        isRawTypePossible(rawType, variableType) {
            let found = this.mappingTable.find(mapping => mapping.raw === rawType);

            if (!found) {
                throw new Error("Column raw type to data variable mapping table does contain raw type: " + rawType);
            }
            return found.variableTypes.includes(variableType);
        },

        findBestMatchingVariable(mapping, autoMapped = false) {

            // Find best match using column name
            const results = findBestMatch(mapping.column.name, this.mappableDataVariables.map(v => v.name));

            if (results.bestMatch.rating > minMatchScore) {

                const dataVariable = this.dataVariables.find((dataVariable => dataVariable.name === results.bestMatch.target));

                if (!dataVariable) {
                    Bus.$emit('flash-message', {type: 'error', message: 'Mapped data point could not be found'});
                    throw new Error("Could not find data variable " + dataVariable.name);
                }

                mapping.variable.possibleTypes = this.getPossibleVariableTypes(mapping.column.metadata.possibleTypes);

                // TODO: Should also check results.ratings for potential other matches
                if (this.isRawTypePossible(mapping.column.metadata.bestType, dataVariable.type)) {

                    mapping.variable.id = dataVariable.id;
                    mapping.variable.name = dataVariable.name;
                    mapping.variable.type = dataVariable.type;
                    mapping.column.metadata.autoMapped = autoMapped;

                    // Map checkbox choice columns
                    if (mapping.variable.type === 'checkbox') {

                        const dataVariableOptionsNames = dataVariable.options.map(option => option.name);
                        const results = findBestMatch(mapping.column.optionName, dataVariableOptionsNames);

                        if (results.bestMatch.rating > minMatchScore) {

                            const option = dataVariable.options.find(option => {
                                return option.name === results.bestMatch.target;
                            });

                            mapping.variable.optionId = option.id;
                            mapping.variable.optionName = option.name;
                        }
                    }

                    this.validate(dataVariable, mapping);
                }
            }
        },

    }
}
</script>
